/* ============================================================
   NOTES  —  SESAAT Studio v3.9
   ============================================================
   Fail ini berkhidmat kepada DUA laluan, dikawal oleh `modSebenar`:

     modSebenar = false  →  /notes-v3.9   (PELAN reka bentuk)
       Nota contoh dalam ingatan browser sahaja. Tiada Firestore,
       tiada auto-sync. Hilang bila halaman ditutup — itu memang
       dikehendaki: ia pelan, bukan ganti.

     modSebenar = true   →  /notes        (HALAMAN HIDUP)
       Nota sebenar daripada `users/{uid}/notes`, auto-sync semua
       peranti. Label pelan dibuang; butang menu telefon muncul.

   ⛔ SATU rupa sahaja. Kad, butang dan susunan dikongsi kedua-dua
      mod — dua sumber data ditukar kepada bentuk dalaman yang sama
      oleh `dariFirestore`. JANGAN buat salinan kedua fail ini untuk
      app sebenar; dua salinan akan terpesong dan halaman hidup
      berhenti sama rupa dengan prototaipnya.

   ── Keputusan user (2026-08-10) ──────────────────────────────
   · Kotak tambah SENTIASA TERBUKA. Prototaip asal melipatnya di
     belakang butang "Nota baharu"; itu menambah satu klik pada
     kerja yang paling kerap dibuat, jadi ia dibatalkan.
   · Baris kosong DIBUANG semasa papar (ikut prototaip). Data
     dalam Firestore tidak diubah — hanya paparan.
   · Senarai KUNCI 3 lajur pada semua saiz skrin.
   · Edit berlaku DALAM kad, bukan tetingkap berasingan.

   ── Dikekalkan daripada rupa lama (NotesView.jsx) ─────────────
   Setiap satu ini pernah wujud dan tidak boleh hilang:
     · pautan dalam nota kekal boleh klik — pelanggan menyimpan
       link TikTok di sini, jadi teks mati bermakna kerja tambahan
       untuk mereka
     · masa relatif setiap nota
     · butang salin pada tajuk DAN pada setiap baris
     · Ctrl / Cmd + Enter untuk simpan nota baharu
     · pengesahan sebelum padam
     · carian merentas tajuk dan isi + butang kosongkan
     · butang menu pada telefon

   ⚠️ `updatedAt` WAJIB ditulis pada SETIAP tulisan. Panel Quick
      Notes dalam Workspace menyusun nota ikut medan itu — berhenti
      menulisnya dan susunan panel itu jadi rawak tanpa sesiapa
      perasan di halaman ini.
   ============================================================ */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Pin, Zap, Copy, Check, Trash2, X, Pencil, Menu, Plus, MoreHorizontal } from 'lucide-react';
import { collection, onSnapshot, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';

import { db, auth } from '../firebase';
import { renderTextWithLinks } from '../utils/notesHelpers';
import { useT } from '../i18n';

import '../design/tokens.css';
import { Btn, IconBtn, Field, Card, CardBody, EmptyState, Segmented, SuisBahasa } from '../design/ui';

/* Bentuk dalaman satu nota:
     { id, tajuk, isi, pin, pantas, masa }
   Medan Firestore (title/text/pinned/isQuickNote/createdAt) ditukar ke
   bentuk ini sekali sahaja, di satu tempat. */
const dariFirestore = (d) => ({
    id: d.id,
    tajuk: d.title || '',
    isi: d.text || '',
    pin: !!d.pinned,
    pantas: !!d.isQuickNote,
    masa: d.createdAt || 0,
});

/* Masa relatif — SENGAJA salinan tempatan, bukan `formatRelative` daripada
   utils/notesHelpers. Helper itu dikongsi dengan rupa lama DAN panel Quick
   Notes dalam Workspace, kedua-duanya kekal berbahasa Melayu; menukarnya di
   sana bermakna menukar dua tempat yang tidak diminta.

   ⚠️ `t` DIHANTAR MASUK sebagai parameter kerana ini fungsi aras-modul —
      aras-modul tidak boleh memanggil useT(). Pemanggil (KadNota) sudah
      mempunyai `t` daripada hook. */
const masaRelatif = (t, ts) => {
    if (!ts) return '';
    const beza = Math.max(0, Date.now() - ts);
    const minit = Math.floor(beza / 60000);
    const jam = Math.floor(minit / 60);
    const hari = Math.floor(jam / 24);
    if (minit < 1) return t('notes.time.now');
    if (minit < 60) return t('notes.time.minutes', { n: minit });
    if (jam < 24) return t('notes.time.hours', { n: jam });
    if (hari === 1) return t('notes.time.yesterday');
    if (hari < 7) return t('notes.time.days', { n: hari });
    return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const JAM = 3600000;
const KINI = Date.now();

const NOTA_CONTOH = [
    {
        id: 'n1', pin: true, pantas: true, masa: KINI - 2 * JAM,
        tajuk: 'Hook yang menjadi',
        isi: 'Korang tau tak kenapa serum ni laku gila?\nAku dah try 3 minggu, hasilnya memang nampak.\nStok tinggal sikit je kat bawah ni.',
    },
    {
        id: 'n2', pin: true, pantas: false, masa: KINI - 26 * JAM,
        tajuk: 'Tetapan Flow untuk GOYANG',
        isi: 'Guna template background putih.\nHand gender ikut produk — barang dapur guna perempuan.',
    },
    {
        id: 'n3', pin: false, pantas: false, masa: KINI - 3 * 24 * JAM,
        tajuk: 'Senarai produk nak scrape minggu ni',
        isi: 'Serum vitamin C\nTudung bawal cotton\nMinyak wangi attar\nBotol termos keluli',
    },
    {
        id: 'n4', pin: false, pantas: true, masa: KINI - 5 * 24 * JAM,
        tajuk: 'Caption TikTok — gaya soalan',
        isi: 'Siapa lagi yang muka berminyak macam aku dulu?\nTry benda ni dulu sebelum beli yang mahal.',
    },
];

const GRID = { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14 };

const medanGaya = {
    width: '100%', background: 'var(--s-ground)', color: 'var(--s-ink)',
    border: '1px solid var(--s-line-strong)', borderRadius: 'var(--s-r-sm)',
    padding: '9px 11px', fontSize: 13.5, fontFamily: 'inherit', outline: 'none',
};

/* Medan dalam kotak tulis — TELUS, tiada bingkai sendiri. Kad itulah
   bingkainya (gaya Google Keep). Bingkai di dalam bingkai ialah sebab kotak
   itu nampak kasar sebelum ini. */
const medanTelus = {
    width: '100%', background: 'transparent', color: 'var(--s-ink)',
    border: 0, padding: 0, fontFamily: 'inherit', outline: 'none',
};

const butangIkon = {
    padding: 5, background: 'none', border: 0, cursor: 'pointer',
    color: 'var(--s-ink-3)', display: 'grid', placeItems: 'center',
    borderRadius: 'var(--s-r-sm)',
};

/* ---------- Satu baris nota + butang salinnya ---------------------- */
function BarisSalin({ teks, disalin, onSalin }) {
    const t = useT();
    return (
        <div className="s-noteline" style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--s-ink-2)', lineHeight: 1.6, overflowWrap: 'anywhere' }}>
                {renderTextWithLinks(teks, 's-link')}
            </span>
            <button
                type="button"
                aria-label={t('notes.copyLine')}
                title={t('notes.copyLine')}
                onClick={(e) => { e.stopPropagation(); onSalin(); }}
                className="s-noteline__copy"
                style={{
                    padding: 3, background: 'none', border: 0, cursor: 'pointer',
                    color: disalin ? 'var(--s-ok)' : 'var(--s-ink-3)', display: 'grid', placeItems: 'center',
                }}
            >
                {disalin ? <Check size={13} strokeWidth={2} /> : <Copy size={13} strokeWidth={1.9} />}
            </button>
        </div>
    );
}

/* ---------- Borang edit dalam kad ----------------------------------
   Draf hidup DI DALAM borang ini, bukan di halaman. Borang hanya wujud
   semasa nota itu sedang diedit, jadi setiap kali ia dibuka draf bermula
   segar daripada nota — dan taipan tidak terpadam bila nota LAIN berubah
   (setiap perubahan nota mencetuskan bacaan baharu untuk seluruh senarai). */
function BorangEdit({ tajukAwal, isiAwal, onSimpan, onBatal }) {
    const t = useT();
    const [tajuk, setTajuk] = useState(tajukAwal);
    const [isi, setIsi] = useState(isiAwal);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input
                value={tajuk}
                onChange={(e) => setTajuk(e.target.value)}
                placeholder={t('notes.titlePlaceholder')}
                style={{ ...medanGaya, fontWeight: 600 }}
            />
            <textarea
                autoFocus
                value={isi}
                onChange={(e) => setIsi(e.target.value)}
                rows={4}
                placeholder={t('notes.bodyPlaceholder')}
                style={{ ...medanGaya, resize: 'vertical', lineHeight: 1.6 }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Btn size="sm" variant="ghost" onClick={onBatal}>{t('common.cancel')}</Btn>
                <Btn size="sm" onClick={() => onSimpan(tajuk, isi)}>{t('common.save')}</Btn>
            </div>
        </div>
    );
}

/* ---------- Kad nota ------------------------------------------------ */
function KadNota({
    n, mengedit, onMulaEdit, onBatalEdit, onSimpanEdit,
    onToggle, onPadam, disalin, onSalin,
    menuBuka, onToggleMenu, onTutupMenu,
}) {
    const t = useT();

    /* Baris kosong dibuang daripada paparan (keputusan user 2026-08-10).
       Teks asal dalam Firestore kekal utuh — buka edit dan perenggan
       asalnya masih ada. */
    const baris = n.isi.split('\n').filter((b) => b.trim());

    if (mengedit) {
        return (
            <Card style={{ display: 'flex', flexDirection: 'column' }}>
                <CardBody>
                    <BorangEdit
                        tajukAwal={n.tajuk}
                        isiAwal={n.isi}
                        onSimpan={onSimpanEdit}
                        onBatal={onBatalEdit}
                    />
                </CardBody>
            </Card>
        );
    }

    return (
        /* `overflow: visible` membatalkan `overflow: hidden` yang dibawa oleh
           .s-card. Tanpa ia, menu ⋯ terpotong pada bibir kad — ia terbuka,
           tetapi hanya jalur nipisnya sahaja kelihatan. */
        <Card style={{ display: 'flex', flexDirection: 'column', overflow: 'visible' }}>
            <CardBody>
                <div className="s-noteline" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                            <h3 className="s-cardname" style={{ flex: 1, minWidth: 0, marginBottom: 3, overflowWrap: 'anywhere' }}>
                                {n.tajuk || t('notes.untitled')}
                            </h3>
                            {n.tajuk && (
                                <button
                                    type="button"
                                    aria-label={t('notes.copyTitle')}
                                    title={t('notes.copyTitle')}
                                    onClick={(e) => { e.stopPropagation(); onSalin(`${n.id}_t`, n.tajuk); }}
                                    className="s-noteline__copy"
                                    style={{
                                        padding: 3, background: 'none', border: 0, cursor: 'pointer', flex: 'none',
                                        color: disalin === `${n.id}_t` ? 'var(--s-ok)' : 'var(--s-ink-3)',
                                        display: 'grid', placeItems: 'center',
                                    }}
                                >
                                    {disalin === `${n.id}_t` ? <Check size={13} strokeWidth={2} /> : <Copy size={13} strokeWidth={1.9} />}
                                </button>
                            )}
                        </div>
                        <p className="s-dim">{masaRelatif(t, n.masa)}</p>
                    </div>

                    <div style={{ display: 'flex', gap: 2, flex: 'none' }}>
                        {/* Quick Notes — nota bertanda muncul dalam panel di Workspace
                            semasa pelanggan mengedit prompt. Keadaan HIDUP diberi latar
                            berwarna, bukan sekadar ikon bertukar warna: dengan empat ikon
                            berturut-turut, perbezaan warna sahaja terlalu senyap untuk
                            memberitahu suis mana yang aktif. */}
                        <button
                            type="button"
                            aria-pressed={n.pantas}
                            aria-label={t(n.pantas ? 'notes.quickRemove' : 'notes.quickAdd')}
                            title={t(n.pantas ? 'notes.quickRemoveHint' : 'notes.quickAddHint')}
                            onClick={() => onToggle(n, 'pantas')}
                            style={{
                                ...butangIkon,
                                background: n.pantas ? 'var(--s-brand-soft)' : 'none',
                                color: n.pantas ? 'var(--s-brand)' : 'var(--s-ink-3)',
                            }}
                        >
                            <Zap size={14} strokeWidth={n.pantas ? 2.4 : 1.9} />
                        </button>
                        <button
                            type="button"
                            aria-pressed={n.pin}
                            aria-label={t(n.pin ? 'notes.unpin' : 'notes.pinNote')}
                            title={t(n.pin ? 'notes.unpin' : 'notes.pinHint')}
                            onClick={() => onToggle(n, 'pin')}
                            style={{
                                ...butangIkon,
                                background: n.pin ? 'var(--s-fill-hover)' : 'none',
                                color: n.pin ? 'var(--s-ink)' : 'var(--s-ink-3)',
                            }}
                        >
                            <Pin size={14} strokeWidth={n.pin ? 2.4 : 1.9} />
                        </button>
                        <button
                            type="button"
                            aria-label={t('notes.editNote')}
                            title={t('notes.editNote')}
                            onClick={onMulaEdit}
                            style={butangIkon}
                        >
                            <Pencil size={14} strokeWidth={1.9} />
                        </button>
                        {/* ⛔ PADAM TIDAK BOLEH DUDUK DALAM BARIS INI (arahan
                            user 2026-08-10). Dulu tong sampah berdiri betul-betul
                            di sebelah pensel: tersasar satu ikon sahaja dan niat
                            "nak edit" jadi "nak padam" — pada telefon jarak itu
                            lebih kecil daripada hujung jari. Nota pula TIADA
                            undo. Kotak pengesahan bukan pelindung yang cukup;
                            ia ditekan OK secara refleks. Jadi padam dialihkan
                            ke dalam menu ini (corak yang sama seperti Google
                            Keep), dan pengesahan DIKEKALKAN. */}
                        <span style={{ position: 'relative', display: 'inline-flex' }}>
                            <button
                                type="button"
                                aria-label={t('notes.more')}
                                aria-haspopup="menu"
                                aria-expanded={!!menuBuka}
                                title={t('notes.more')}
                                onClick={onToggleMenu}
                                style={{
                                    ...butangIkon,
                                    background: menuBuka ? 'var(--s-fill-hover)' : 'none',
                                    color: menuBuka ? 'var(--s-ink)' : 'var(--s-ink-3)',
                                }}
                            >
                                <MoreHorizontal size={14} strokeWidth={1.9} />
                            </button>

                            {menuBuka && (
                                <>
                                    {/* Lapisan tak nampak — klik di mana-mana
                                        sahaja menutup menu. Lebih dipercayai
                                        daripada memasang pendengar pada dokumen. */}
                                    <div
                                        onClick={onTutupMenu}
                                        style={{ position: 'fixed', inset: 0, zIndex: 29 }}
                                    />
                                    <div className="s-pop" role="menu" style={{ left: 'auto', right: 0, minWidth: 158 }}>
                                        <button
                                            type="button"
                                            role="menuitem"
                                            className="s-pop__row"
                                            onClick={() => { onTutupMenu(); onPadam(n); }}
                                            style={{ color: 'var(--s-stop)' }}
                                        >
                                            <Trash2 size={14} strokeWidth={1.9} />
                                            {t('notes.deleteNote')}
                                        </button>
                                    </div>
                                </>
                            )}
                        </span>
                    </div>
                </div>

                {/* Setiap baris boleh disalin sendiri — ini keupayaan halaman
                    asal yang paling kerap diguna (satu hook, satu caption),
                    jadi ia dikekalkan bulat-bulat. */}
                {baris.length > 0 && (
                    <div style={{
                        background: 'var(--s-ground)', border: '1px solid var(--s-line)',
                        borderRadius: 'var(--s-r)', padding: '8px 10px',
                        display: 'flex', flexDirection: 'column', gap: 2,
                    }}>
                        {baris.map((b, i) => (
                            <BarisSalin
                                key={i}
                                teks={b}
                                disalin={disalin === `${n.id}_${i}`}
                                onSalin={() => onSalin(`${n.id}_${i}`, b)}
                            />
                        ))}
                    </div>
                )}
            </CardBody>
        </Card>
    );
}

/* ---------- Rangka semasa memuat ------------------------------------
   Bentuk yang sama dengan kad sebenar supaya halaman tidak melompat
   bila nota sampai. */
function KadRangka() {
    const jalur = (lebar, tinggi = 11) => (
        <span style={{ display: 'block', width: lebar, height: tinggi, borderRadius: 5, background: 'var(--s-fill-hover)' }} />
    );
    return (
        <Card className="s-skel">
            <CardBody style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {jalur('58%', 13)}
                {jalur('30%', 9)}
                <span style={{ display: 'block', height: 8 }} />
                {jalur('92%')}
                {jalur('78%')}
                {jalur('85%')}
            </CardBody>
        </Card>
    );
}

export default function NotesV39View({ modSebenar = false, onMenuClick }) {
    const t = useT();
    const [nota, setNota] = useState(() => (modSebenar ? [] : NOTA_CONTOH));
    const [memuat, setMemuat] = useState(modSebenar);
    const [cari, setCari] = useState('');
    /* Telefon sahaja: carian bermula sebagai butang ikon dan hanya menjadi
       medan penuh selepas diketuk. Pada desktop nilai ini tidak bermakna —
       medan carian sentiasa kelihatan di sana. */
    const [cariBuka, setCariBuka] = useState(false);
    const [tapis, setTapis] = useState('semua');
    const [disalin, setDisalin] = useState(null);

    const [tajukBaru, setTajukBaru] = useState('');
    const [isiBaru, setIsiBaru] = useState('');
    const [pinBaru, setPinBaru] = useState(false);
    const [pantasBaru, setPantasBaru] = useState(false);
    const [menyimpan, setMenyimpan] = useState(false);
    const [editId, setEditId] = useState(null);
    /* Satu sahaja menu ⋯ boleh terbuka pada satu masa — sebab itu ia disimpan
       di sini sebagai id, bukan di dalam setiap kad. */
    const [menuId, setMenuId] = useState(null);

    /* Kotak tulis hanya muncul selepas "Nota baharu" ditekan (arahan user
       2026-08-10) — dan kursor terus masuk ke dalamnya. Jadi klik itu tetap
       satu klik sahaja: tekan, terus boleh menampal link. Tanpa fokus
       automatik ini, butang tersebut menambah kerja dan bukan mengurangkan. */
    const [bukaTulis, setBukaTulis] = useState(false);
    const kotakTulisRef = useRef(null);

    useEffect(() => {
        if (bukaTulis) kotakTulisRef.current?.focus();
    }, [bukaTulis]);

    const tutupTulis = () => {
        setBukaTulis(false);
        setTajukBaru('');
        setIsiBaru('');
        setPinBaru(false);
        setPantasBaru(false);
    };

    /* ── Bacaan langsung (mod sebenar sahaja) ─────────────────────────
       Susunan dibuat di sini, bukan melalui query — koleksi nota kecil,
       dan menyusun di server memerlukan index tambahan tanpa faedah. */
    useEffect(() => {
        if (!modSebenar) return undefined;
        const pengguna = auth.currentUser;
        if (!pengguna) { setMemuat(false); return undefined; }

        const rujuk = collection(db, 'users', pengguna.uid, 'notes');
        const unsub = onSnapshot(
            rujuk,
            (snap) => {
                const senarai = snap.docs.map((d) => dariFirestore({ id: d.id, ...d.data() }));
                senarai.sort((a, b) => {
                    if (a.pin !== b.pin) return a.pin ? -1 : 1;   // bersemat dahulu
                    return (b.masa || 0) - (a.masa || 0);          // terbaru dahulu
                });
                setNota(senarai);
                setMemuat(false);
            },
            (err) => {
                console.error('[Notes] load error:', err);
                setMemuat(false);
            },
        );
        return () => unsub();
    }, [modSebenar]);

    const rujukNota = (id) => doc(db, 'users', auth.currentUser.uid, 'notes', id);

    /* ── Tambah ──────────────────────────────────────────────────── */
    const tambah = async () => {
        const tajuk = tajukBaru.trim();
        const isi = isiBaru.trim();
        if ((!tajuk && !isi) || menyimpan) return;

        /* Kotak tutup semula selepas nota disimpan — termasuk dua suis, supaya
           nota berikutnya tidak terbawa tetapan nota sebelumnya tanpa
           disedari. */
        const kosongkan = tutupTulis;

        if (!modSebenar) {
            setNota((p) => [
                { id: `n${Date.now()}`, pin: pinBaru, pantas: pantasBaru, tajuk, isi, masa: Date.now() },
                ...p,
            ]);
            kosongkan();
            return;
        }

        if (!auth.currentUser) return;
        setMenyimpan(true);
        try {
            const kini = Date.now();
            await addDoc(collection(db, 'users', auth.currentUser.uid, 'notes'), {
                title: tajuk,
                text: isi,
                pinned: pinBaru,
                isQuickNote: pantasBaru,
                createdAt: kini,
                updatedAt: kini,
            });
            kosongkan();
        } catch (err) {
            console.error('[Notes] add failed:', err);
            alert(t('notes.errAdd'));
        } finally {
            setMenyimpan(false);
        }
    };

    /* ── Pin / Quick Notes ───────────────────────────────────────── */
    const toggle = async (n, medan) => {
        if (!modSebenar) {
            setNota((p) => p.map((x) => (x.id === n.id ? { ...x, [medan]: !x[medan] } : x)));
            return;
        }
        if (!auth.currentUser) return;
        const medanDb = medan === 'pin' ? 'pinned' : 'isQuickNote';
        try {
            await updateDoc(rujukNota(n.id), { [medanDb]: !n[medan], updatedAt: Date.now() });
        } catch (err) {
            console.error('[Notes] toggle failed:', err);
            alert(t('notes.errUpdate'));
        }
    };

    /* ── Edit ────────────────────────────────────────────────────── */
    const simpanEdit = async (n, tajukBaharu, isiBaharu) => {
        const tajuk = tajukBaharu.trim();
        const isi = isiBaharu.trim();

        /* Dua-dua kosong → batal sahaja. Nota TIDAK dipadam; membuang nota
           kerana medannya dikosongkan ialah kejutan, bukan ciri. */
        if (!tajuk && !isi) { setEditId(null); return; }

        if (!modSebenar) {
            setNota((p) => p.map((x) => (x.id === n.id ? { ...x, tajuk, isi } : x)));
            setEditId(null);
            return;
        }

        if (!auth.currentUser) { setEditId(null); return; }
        try {
            await updateDoc(rujukNota(n.id), { title: tajuk, text: isi, updatedAt: Date.now() });
        } catch (err) {
            console.error('[Notes] update failed:', err);
            alert(t('notes.errUpdate'));
        } finally {
            setEditId(null);
        }
    };

    /* ── Padam ───────────────────────────────────────────────────── */
    const padam = async (n) => {
        if (!modSebenar) {
            setNota((p) => p.filter((x) => x.id !== n.id));
            return;
        }
        if (!auth.currentUser) return;
        if (!confirm(t('notes.confirmDelete'))) return;
        try {
            await deleteDoc(rujukNota(n.id));
        } catch (err) {
            console.error('[Notes] delete failed:', err);
            alert(t('notes.errDelete'));
        }
    };

    /* ── Salin ───────────────────────────────────────────────────── */
    const salin = async (kunci, teks) => {
        try {
            await navigator.clipboard.writeText(teks);
            setDisalin(kunci);
            setTimeout(() => setDisalin((k) => (k === kunci ? null : k)), 1500);
        } catch (err) {
            console.error('[Notes] copy failed:', err);
        }
    };

    /* ── Carian + penapis (dalam browser, tiada bacaan tambahan) ──── */
    const ditapis = useMemo(() => {
        const q = cari.trim().toLowerCase();
        return nota
            .filter((n) => (tapis === 'pantas' ? n.pantas : tapis === 'pin' ? n.pin : true))
            .filter((n) => !q || n.tajuk.toLowerCase().includes(q) || n.isi.toLowerCase().includes(q));
    }, [nota, cari, tapis]);

    const jumlahPantas = nota.filter((n) => n.pantas).length;
    const jumlahPin = nota.filter((n) => n.pin).length;
    const bersemat = ditapis.filter((n) => n.pin);
    const biasa = ditapis.filter((n) => !n.pin);

    const sifatKad = (n) => ({
        n,
        mengedit: editId === n.id,
        onMulaEdit: () => setEditId(n.id),
        onBatalEdit: () => setEditId(null),
        onSimpanEdit: (t, i) => simpanEdit(n, t, i),
        onToggle: toggle,
        onPadam: padam,
        disalin,
        onSalin: salin,
        menuBuka: menuId === n.id,
        onToggleMenu: () => setMenuId((m) => (m === n.id ? null : n.id)),
        onTutupMenu: () => setMenuId(null),
    });

    /* Tekan "Nota baharu" semasa kotak sudah terbuka pun mesti membawa kursor
       balik ke dalamnya — kalau tidak butang itu nampak rosak. */
    const bukaKotakTulis = () => {
        setBukaTulis(true);
        kotakTulisRef.current?.focus();
    };

    /* Kotak tulis — gaya Google Keep. Ia terbuka TERUS dalam bentuk penuh
       (tajuk + isi + dua pilihan + butang) sebagai panel lebar di TENGAH
       halaman (arahan user 2026-08-10). Tiada lagi keadaan 'sebaris dahulu,
       kembang bila menaip': pelanggan sudah menyatakan niatnya dengan menekan
       'New Note', jadi menyorokkan separuh borang selepas itu cuma menambah
       satu langkah lagi.

       ⚠️ Ditulis sebagai ELEMEN, bukan komponen yang ditakrif dalam fungsi ini.
          Komponen yang ditakrif semula setiap render dianggap jenis BAHARU oleh
          React, jadi ia dipasang semula setiap kali — dan kursor terpelanting
          keluar sebaik huruf pertama ditaip. */
    const kotakTulis = (
        /* Lebar mengikut skrin, bukan satu nombor tetap. `clamp` yang menahan
           dua hujungnya: cukup lebar pada skrin besar, dan tidak pernah
           menghimpit kawalan di lajur kanan sampai ia terpaksa berpatah. */
        <Card className="s-notehead__tulis" style={{ width: 'clamp(360px, 40vw, 760px)', maxWidth: '100%' }}>
            <CardBody style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input
                    className="s-notefield"
                    value={tajukBaru}
                    onChange={(e) => setTajukBaru(e.target.value)}
                    placeholder={t('notes.titlePlaceholder')}
                    style={{ ...medanTelus, fontSize: 15, fontWeight: 600 }}
                />

                <textarea
                    ref={kotakTulisRef}
                    className="s-notefield"
                    value={isiBaru}
                    onChange={(e) => setIsiBaru(e.target.value)}
                    onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') tambah(); }}
                    rows={5}
                    placeholder={t('notes.bodyPlaceholder')}
                    style={{
                        ...medanTelus, fontSize: 13.5, lineHeight: 1.6,
                        /* resize:none — pemegang seret di bucu kanan bawah itulah
                           yang paling ketara membuatkan kotak ini nampak seperti kotak
                           mentah, bukan sebahagian kad. */
                        resize: 'none',
                    }}
                />

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {/* Pilihan ditetapkan SEBELUM simpan — tanpa ini pelanggan terpaksa
                        simpan dahulu, cari kad itu dalam senarai, kemudian menekan
                        ikonnya. Medan yang ditulis sama persis dengan ikon pada kad:
                        isQuickNote dan pinned. */}
                    <button
                        type="button"
                        aria-pressed={pantasBaru}
                        title={t('notes.quickHint')}
                        onClick={() => setPantasBaru((v) => !v)}
                        className={`s-chip${pantasBaru ? ' s-chip--on' : ''}`}
                        style={pantasBaru ? {
                            background: 'var(--s-brand-soft)',
                            color: 'var(--s-brand)',
                            borderColor: 'transparent',
                        } : undefined}
                    >
                        <Zap size={13} strokeWidth={pantasBaru ? 2.4 : 1.9} />
                        <span>{t('notes.quickNotes')}</span>
                    </button>
                    <button
                        type="button"
                        aria-pressed={pinBaru}
                        title={t('notes.pinHint')}
                        onClick={() => setPinBaru((v) => !v)}
                        className={`s-chip${pinBaru ? ' s-chip--on' : ''}`}
                    >
                        <Pin size={13} strokeWidth={pinBaru ? 2.4 : 1.9} />
                        <span>{t('notes.pin')}</span>
                    </button>

                    {/* Jalan keluar. Tanpa ia, kotak yang terbuka tersilap tidak boleh
                        ditutup semula. */}
                    <Btn size="sm" variant="quiet" onClick={tutupTulis} style={{ marginLeft: 'auto' }}>
                        {t('common.cancel')}
                    </Btn>
                    <Btn
                        size="sm"
                        onClick={tambah}
                        disabled={(!tajukBaru.trim() && !isiBaru.trim()) || menyimpan}
                    >
                        {menyimpan ? t('common.saving') : t('notes.addNote')}
                    </Btn>
                </div>
            </CardBody>
        </Card>
    );

    /* Bar alat halaman — duduk SEBARIS dengan tajuk, di sebelah kanannya
       (rujukan: app.cal.com/availability). Turutan: butang tindakan · penapis ·
       carian di hujung sekali. */
    const alatBar = (
        <>
            {/* Butang HILANG sebaik kotak tulis terbuka (arahan user 2026-08-10)
                — kotak itu mengambil alih kerjanya, jadi halaman tidak pernah
                memapar dua pintu masuk yang sama serentak. */}
            {!bukaTulis && (
                <Btn size="sm" icon={<Plus size={14} strokeWidth={2} />} onClick={bukaKotakTulis}>
                    {t('notes.newNote')}
                </Btn>
            )}

            <div className={`s-notehead__alat${cariBuka ? ' s-notehead__alat--cari' : ''}`}>
                {/* Penapis — TAMBAHAN. Rupa lama tiada cara melihat nota mana yang
                    menemani pelanggan semasa bekerja (Quick Notes) atau nota mana
                    yang disematkan; mereka terpaksa mengimbas ikon satu per satu. */}
                <Segmented
                    value={tapis}
                    onChange={setTapis}
                    options={[
                        { value: 'semua', label: t('notes.filter.all', { n: nota.length }) },
                        { value: 'pantas', label: t('notes.filter.quick', { n: jumlahPantas }) },
                        { value: 'pin', label: t('notes.filter.pinned', { n: jumlahPin }) },
                    ]}
                />

                {/* Carian di hujung sekali. Pada telefon ia disorok oleh CSS
                    sehingga butang ikon di bawah diketuk. */}
                <Field
                    className="s-cari-medan"
                    icon={<Search size={15} strokeWidth={1.9} />}
                    placeholder={t('notes.search')}
                    value={cari}
                    onChange={(e) => setCari(e.target.value)}
                    style={{ maxWidth: 300 }}
                    trailing={(cari || cariBuka) ? (
                        <button
                            type="button"
                            aria-label={t('notes.clearSearch')}
                            title={t('notes.clearSearch')}
                            /* Kosongkan teks DAN tutup semula medan. Pada telefon
                               ini satu-satunya jalan keluar daripada mod carian;
                               tanpa ia medan itu terperangkap terbuka. */
                            onClick={() => { setCari(''); setCariBuka(false); }}
                            style={{ ...butangIkon, padding: 2, flex: 'none' }}
                        >
                            <X size={14} strokeWidth={2} />
                        </button>
                    ) : null}
                />

                {/* Telefon sahaja (CSS yang menyorokkannya pada desktop) —
                    medan carian penuh tidak muat di sebelah tiga tab pada skrin
                    375px, dan tab itu lebih kerap diguna daripada carian. */}
                <IconBtn
                    className="s-cari-ikon"
                    label={t('notes.search')}
                    icon={<Search size={16} strokeWidth={1.9} />}
                    onClick={() => setCariBuka(true)}
                />

                {!modSebenar && <span className="s-dim">{t('notes.planNote')}</span>}
            </div>
        </>
    );

    const isi = (
        <div className="sesaat-m-tight" style={{ padding: '26px 28px 48px' }}>

            {/* Butang menu telefon. Route /notes ada dalam
                ROUTES_WITH_OWN_MENU_BUTTON (App.jsx), jadi header kongsi
                TIDAK melukis hamburger untuk halaman ini — kalau butang ini
                hilang, pelanggan telefon tiada jalan membuka menu. */}
            {modSebenar && onMenuClick && (
                <button
                    onClick={onMenuClick}
                    aria-label={t('notes.openMenu')}
                    className="md:hidden"
                    style={{
                        background: 'transparent', border: 0, padding: 6, marginBottom: 10,
                        borderRadius: 8, color: 'var(--s-ink-2)', cursor: 'pointer',
                    }}
                >
                    <Menu size={20} strokeWidth={1.9} />
                </button>
            )}

            {/* Kepala halaman TIGA lajur — tajuk · kotak tulis · kawalan,
                ketiga-tiganya bermula pada garis atas yang SAMA (arahan user
                2026-08-10). Ia TIDAK guna komponen PageHeader kongsi: komponen
                itu hanya tahu dua sisi (tajuk di kiri, tindakan di kanan), dan
                kotak tulis di tengah tiada tempat di dalamnya.

                Tiada subheader — baris penerangan hanya menolak kerja sebenar
                ke bawah pada halaman yang isinya sudah terang: nota.

                ⚠️ Lajur tengah mesti WUJUD walaupun kosong, kalau tidak kawalan
                   melompat masuk ke lajur tengah dan berhenti berada di kanan. */}
            <div className="s-notehead">
                <h1 className="s-title">{t('notes.title')}</h1>
                {bukaTulis ? kotakTulis : <span />}
                {/* Pil bahasa ELEMEN TERAKHIR — kedudukan sama seperti PageHeader. */}
                <div className="s-notehead__kanan">{alatBar}<SuisBahasa /></div>
            </div>


            {memuat ? (
                <div style={GRID}>
                    <KadRangka /><KadRangka /><KadRangka />
                </div>
            ) : nota.length === 0 ? (
                <EmptyState
                    title={t('notes.emptyTitle')}
                    action={(
                        <Btn size="sm" icon={<Plus size={14} strokeWidth={2} />} onClick={bukaKotakTulis}>
                            {t('notes.newNote')}
                        </Btn>
                    )}
                >
                    {t('notes.emptyDesc')}
                </EmptyState>
            ) : ditapis.length === 0 ? (
                <EmptyState title={t('notes.noMatchTitle')}>
                    {tapis === 'pantas' && !cari.trim()
                        ? t('notes.noMatchQuick')
                        : tapis === 'pin' && !cari.trim()
                            ? t('notes.noMatchPinned')
                            : t('notes.noMatchSearch')}
                </EmptyState>
            ) : (
                <>
                    {bersemat.length > 0 && (
                        <div style={{ marginBottom: 22 }}>
                            {/* Tajuk ini tidak dipapar dalam tab Pinned — di sana
                                SEMUA nota bersemat, jadi ia hanya mengulang nama
                                tab di bawahnya sendiri. */}
                            {tapis !== 'pin' && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
                                    <Pin size={13} strokeWidth={2} style={{ color: 'var(--s-ink-3)' }} />
                                    <span className="s-dim">{t('notes.pinnedSection')}</span>
                                </div>
                            )}
                            <div style={GRID}>
                                {bersemat.map((n) => <KadNota key={n.id} {...sifatKad(n)} />)}
                            </div>
                        </div>
                    )}

                    {biasa.length > 0 && (
                        <div style={GRID}>
                            {biasa.map((n) => <KadNota key={n.id} {...sifatKad(n)} />)}
                        </div>
                    )}
                </>
            )}

        </div>
    );

    /* Mod pelan sudah dibalut ContentPanel oleh StudioShell — balut sekali
       lagi menghasilkan panel bersarang. Mod sebenar tidak melalui
       StudioShell, jadi halaman ini memasang panelnya sendiri: bekas yang
       SAMA, supaya rupanya betul-betul seperti prototaip. `berlatar` wajib
       kerana <main> app lama berlatar terang. */
    /* Bingkai dipasang SEKALI di App.jsx (`BingkaiHalaman`) untuk seluruh
       app; mod pelan pula dibalut StudioShell. Halaman ini tidak memasang
       bingkainya sendiri (tukar 2026-08-11) — dua lapis = kotak dalam kotak. */
    return isi;
}
